'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 20);

      if (currentScrollY > lastScrollY && currentScrollY > 200) {
        // Scrolling down
        setHidden(true);
      } else {
        // Scrolling up
        setHidden(false);
      }
      lastScrollY = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Load Google Translate script
    const script = document.createElement('script');
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);

    // Initialize Google Translate
    (window as any).googleTranslateElementInit = () => {
      new (window as any).google.translate.TranslateElement(
        {
          pageLanguage: 'de',
          includedLanguages: 'en,de,pl,fr,es,it,ru,tr,ar,zh-CN',
          layout: (window as any).google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        'google_translate_element'
      );
      
      // Also initialize mobile version
      new (window as any).google.translate.TranslateElement(
        {
          pageLanguage: 'de',
          includedLanguages: 'en,de,pl,fr,es,it,ru,tr,ar,zh-CN',
          layout: (window as any).google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        'google_translate_element_mobile'
      );
    };

    return () => {
      const existingScript = document.querySelector('script[src*="translate.google.com"]');
      if (existingScript) {
        existingScript.remove();
      }
      delete (window as any).googleTranslateElementInit;
    };
  }, []);

  const navigation = [
    { id: 'nav_home', name: 'Home', href: '/homepage' },
    { id: 'nav_services', name: 'Services', href: '/services' },
    { id: 'nav_team', name: 'Team', href: '/team' },
    { id: 'nav_polen', name: 'Polen', href: '/polen' },
    { id: 'nav_switzerland', name: 'Switzerland', href: '/switzerland' },
    { id: 'nav_contact', name: 'Contact', href: '/contact' },
  ];

  const isActive = (href: string) => pathname === href;

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-700 ${
        scrolled ? 'bg-background/80 backdrop-blur-md shadow-sm' : ''
      } ${hidden ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100'}`}
    >
      <nav className="max-w-8xl mx-auto px-4 md:px-8 lg:px-12 h-20 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group">
          <Image
            src="/assets/images/cyfire-intl-dark-transparent-1775806022629.png"
            alt="Cyfire International Logo"
            width={180}
            height={60}
            className="h-12 w-auto object-contain group-hover:opacity-80 transition-opacity"
            priority
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-4 lg:gap-8">
          {navigation.map((item) => (
            <Link
              key={item.id}
              href={item.href}
              className={`text-sm font-medium tracking-wide transition-all relative whitespace-nowrap ${
                isActive(item.href)
                  ? 'text-primary' :'text-muted-foreground hover:text-primary'
              }`}
            >
              {item.name}
              {isActive(item.href) && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-secondary" />
              )}
            </Link>
          ))}
        </div>

        {/* Language Selector */}
        <div className="hidden md:flex items-center">
          <div className="relative">
            <button
              onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/5 transition-all group whitespace-nowrap"
            >
              <Icon name="GlobeAltIcon" size={14} className="text-primary" />
              <span className="text-xs font-medium">Choose Language</span>
              <Icon 
                name="ChevronDownIcon" 
                size={12} 
                className={`text-primary transition-transform ${
                  languageMenuOpen ? 'rotate-180' : ''
                }`} 
              />
            </button>
            
            {/* Keep widget in DOM but hide with CSS */}
            <div className={`absolute top-full mt-2 right-0 bg-white border-2 border-primary/20 rounded-xl shadow-xl overflow-hidden min-w-[200px] z-50 ${
              languageMenuOpen ? 'block' : 'hidden'
            }`}>
              <div id="google_translate_element" className="[&_.goog-te-gadget]:!p-2 [&_.goog-te-gadget]:!text-sm [&_select]:!border [&_select]:!border-gray-300 [&_select]:!rounded-md [&_select]:!px-2 [&_select]:!py-1"></div>
            </div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center"
          aria-label="Toggle menu"
        >
          <div className="w-6 flex flex-col gap-1.5">
            <span
              className={`w-full h-0.5 bg-foreground transition-all ${
                mobileMenuOpen ? 'rotate-45 translate-y-2' : ''
              }`}
            />
            <span
              className={`w-4 h-0.5 bg-foreground transition-all ${
                mobileMenuOpen ? 'opacity-0' : ''
              }`}
            />
            <span
              className={`w-full h-0.5 bg-foreground transition-all ${
                mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
              }`}
            />
          </div>
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-card border-t border-border">
          <div className="px-6 py-8 space-y-6">
            {navigation.map((item) => (
              <Link
                key={item.id}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block text-lg font-medium ${
                  isActive(item.href) ? 'text-primary' : 'text-foreground hover:text-primary'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Mobile Language Selector */}
            <div className="py-2">
              <button
                onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-primary/30 text-primary hover:bg-primary/5 transition-all w-full justify-center"
              >
                <Icon name="GlobeAltIcon" size={18} className="text-primary" />
                <span className="text-sm font-medium">Choose Language</span>
                <Icon 
                  name="ChevronDownIcon" 
                  size={16} 
                  className={`text-primary transition-transform ${
                    languageMenuOpen ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              
              {/* Keep widget in DOM but hide with CSS */}
              <div className={`mt-2 bg-white border-2 border-primary/20 rounded-xl p-2 ${
                languageMenuOpen ? 'block' : 'hidden'
              }`}>
                <div id="google_translate_element_mobile" className="[&_.goog-te-gadget]:!text-sm [&_select]:!border [&_select]:!border-gray-300 [&_select]:!rounded-md [&_select]:!px-2 [&_select]:!py-1 [&_select]:!w-full"></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}